
// src/store/slices/hourlyLogSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import hourlyLogApi from '../../api/hourlyLogApi';  // adjust path if needed

export const fetchHourlyLogs = createAsyncThunk(
  'hourlyLog/fetchHourlyLogs',
  async ({ page = 1, pageSize = 10, search = '', employee = '' }, { rejectWithValue }) => {
    try {
      const response = await hourlyLogApi.getAll(page, pageSize, search, employee);
      return response;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to load hourly logs';
      return rejectWithValue(msg);
    }
  }
);

export const createHourlyLog = createAsyncThunk(
  'hourlyLog/createHourlyLog',
  async (data, { rejectWithValue }) => {
    try {
      const response = await hourlyLogApi.create(data);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || { detail: 'Failed to create hourly log' });
    }
  }
);

export const updateHourlyLog = createAsyncThunk(
  'hourlyLog/updateHourlyLog',
  async ({ id, ...updates }, { rejectWithValue }) => {
    try {
      const response = await hourlyLogApi.update(id, updates);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || { detail: 'Failed to update hourly log' });
    }
  }
);

export const deleteHourlyLog = createAsyncThunk(
  'hourlyLog/deleteHourlyLog',
  async (id, { rejectWithValue }) => {
    try {
      await hourlyLogApi.delete(id);
      return id;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete hourly log';
      return rejectWithValue(msg);
    }
  }
);

export const exportHourlyLogs = createAsyncThunk(
  'hourlyLog/exportHourlyLogs',
  async (_, { rejectWithValue }) => {
    try {
      const blob = await hourlyLogApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hourly-logs_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue('Export failed');
    }
  }
);

const hourlyLogSlice = createSlice({
  name: 'hourlyLog',
  initialState: {
    hourlyLogs: [], // flattened list of entries (what most tables / lists use)
       pagination: { count: 0, page_size: 10, current_page: 1, total_pages: 1 },
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetHourlyLogs: (state) => {
      state.hourlyLogs = [];
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        pageSize: 10,
        count: 0,
        next: null,
        previous: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchHourlyLogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHourlyLogs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hourlyLogs = action.payload.data || [];           // ← GROUPED SHIFTS (what you want)
        state.pagination = action.payload.pagination || { count: 0, page_size: 10 };
      })
      .addCase(fetchHourlyLogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE – add to top of current page
      .addCase(createHourlyLog.fulfilled, (state, action) => {
        state.hourlyLogs.unshift(action.payload);
        state.pagination.count += 1;
      })

      // UPDATE
      .addCase(updateHourlyLog.fulfilled, (state, action) => {
        const idx = state.hourlyLogs.findIndex((log) => log.id === action.payload.id);
        if (idx !== -1) {
          state.hourlyLogs[idx] = action.payload;
        }
      })

      // DELETE
      .addCase(deleteHourlyLog.fulfilled, (state, action) => {
        state.hourlyLogs = state.hourlyLogs.filter((log) => log.id !== action.payload);
        if (state.pagination.count > 0) state.pagination.count -= 1;
      });
  },
});

export const { clearError, resetHourlyLogs } = hourlyLogSlice.actions;
export default hourlyLogSlice.reducer;